/**
 * Student Report Models matching Flutter domain:
 * /lib/domain/user/student/student_report.dart
 */

export const StudentReportStatus = {
    GENERATING: 'GENERATING',
    SUCCESS: 'SUCCESS',
    FAIL: 'FAIL',
    WARNING: 'WARNING',
    NOT_GENERATED: 'NOT_GENERATED',

    fromString(status) {
        if (!status) return this.NOT_GENERATED;
        switch (String(status).toUpperCase()) {
            case 'GENERATING':
                return this.GENERATING;
            case 'SUCCESS':
                return this.SUCCESS;
            case 'FAIL':
            case 'FAILED':
                return this.FAIL;
            case 'WARNING':
                return this.WARNING;
            default:
                return this.NOT_GENERATED;
        }
    }
};

export class SubjectPerformanceReport {
    constructor({ subjectId = null, subjectName = '', detailedReport = '' } = {}) {
        this.subjectId = subjectId;
        this.subjectName = subjectName;
        this.detailedReport = detailedReport;
    }

    static fromMap(map) {
        if (!map || typeof map !== 'object') {
            return new SubjectPerformanceReport();
        }
        return new SubjectPerformanceReport({
            subjectId: map.subjectId ?? null,
            subjectName: String(map.subjectName || map.name || ''),
            detailedReport: String(map.detailedReport || map.report || map.details || '')
        });
    }

    toMap() {
        return {
            subjectId: this.subjectId,
            subjectName: this.subjectName,
            detailedReport: this.detailedReport
        };
    }
}

export class ReportError {
    constructor({ message = '', userMessage = '' } = {}) {
        this.message = message;
        this.userMessage = userMessage;
    }

    static fromMap(map) {
        if (!map || typeof map !== 'object') return null;
        return new ReportError({
            message: String(map.message || ''),
            userMessage: String(map.userMessage || map.message || '')
        });
    }

    toMap() {
        return {
            message: this.message,
            userMessage: this.userMessage
        };
    }
}

export class ReportContent {
    constructor({
        overallSummary = '',
        performanceOverview = null,
        subjectPerformance = [],
        generalRecommendations = ''
    } = {}) {
        this.overallSummary = overallSummary;
        this.performanceOverview = performanceOverview;
        this.subjectPerformance = subjectPerformance;
        this.generalRecommendations = generalRecommendations;
    }

    static fromMap(map) {
        if (!map || typeof map !== 'object') {
            return new ReportContent();
        }

        let list = [];
        if (Array.isArray(map.subjectPerformance)) {
            list = map.subjectPerformance.map(item => SubjectPerformanceReport.fromMap(item));
        }

        return new ReportContent({
            overallSummary: String(map.overallSummary || ''),
            performanceOverview: map.performanceOverview && typeof map.performanceOverview === 'object'
                ? map.performanceOverview
                : null,
            subjectPerformance: list,
            generalRecommendations: String(map.generalRecommendations || '')
        });
    }

    static fromRawString(raw) {
        return new ReportContent({
            overallSummary: String(raw || ''),
            performanceOverview: null,
            subjectPerformance: [],
            generalRecommendations: ''
        });
    }

    toMap() {
        return {
            overallSummary: this.overallSummary,
            performanceOverview: this.performanceOverview,
            subjectPerformance: this.subjectPerformance.map(e => e.toMap()),
            generalRecommendations: this.generalRecommendations
        };
    }
}

export class StudentReport {
    constructor({
        userId = '',
        admissionNo = '',
        studentId = '',
        academicYear = '',
        Class = null,
        sec = '',
        name = '',
        report = null,
        rawReport = null,
        status = StudentReportStatus.NOT_GENERATED,
        error = null,
        createdAt = null,
        updatedAt = null
    } = {}) {
        this.userId = userId;
        this.admissionNo = admissionNo;
        this.studentId = studentId;
        this.academicYear = academicYear;
        this.Class = Class;
        this.sec = sec;
        this.name = name;
        this.report = report;
        this.rawReport = rawReport;
        this.status = status;
        this.error = error;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
    }

    static fromMap(data) {
        if (!data || typeof data !== 'object') {
            return null;
        }

        let reportObj = null;
        let rawReportString = null;

        if (data.report != null) {
            if (typeof data.report === 'object') {
                reportObj = ReportContent.fromMap(data.report);
            } else if (typeof data.report === 'string') {
                rawReportString = data.report;
                try {
                    const decoded = JSON.parse(rawReportString);
                    if (decoded && typeof decoded === 'object') {
                        reportObj = ReportContent.fromMap(decoded);
                    } else {
                        reportObj = ReportContent.fromRawString(rawReportString);
                    }
                } catch {
                    reportObj = ReportContent.fromRawString(rawReportString);
                }
            }
        } else if (data.rawReport) {
            rawReportString = String(data.rawReport);
            reportObj = ReportContent.fromRawString(rawReportString);
        }

        let errObj = null;
        if (data.error && typeof data.error === 'object') {
            errObj = ReportError.fromMap(data.error);
        }

        return new StudentReport({
            userId: String(data.userId || data.mainUserId || ''),
            admissionNo: data.admissionNo ? String(data.admissionNo) : '',
            studentId: data.studentId ? String(data.studentId) : '',
            academicYear: data.academicYear ? String(data.academicYear) : '',
            Class: data.Class,
            sec: String(data.sec || data.classLabel || ''),
            name: String(data.name || data.studentName || ''),
            report: reportObj,
            rawReport: rawReportString,
            status: StudentReportStatus.fromString(data.status),
            error: errObj,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
        });
    }

    toMap() {
        return {
            userId: this.userId,
            admissionNo: this.admissionNo,
            studentId: this.studentId,
            academicYear: this.academicYear,
            Class: this.Class,
            sec: this.sec,
            name: this.name,
            report: this.report?.toMap(),
            rawReport: this.rawReport,
            status: this.status,
            error: this.error?.toMap(),
            createdAt: this.createdAt,
            updatedAt: this.updatedAt
        };
    }
}
